// Adapted from https://github.com/airbnb/rheostat/blob/master/src/SliderHandle.jsx
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { t } from '@lingui/macro';

import type { Orientation } from '@rover/types/src/Image';

import { PrefixContext } from './Slider';

export type Props = {
  disabled?: boolean;
  handleRef?: () => void;
  orientation?: Orientation;
  role?: string;
  tabIndex?: number;
  'aria-valuemax'?: number;
  'aria-valuemin'?: number;
  'aria-valuenow'?: number;
  'aria-disabled'?: boolean;
  'aria-valuetext'?: string;
  'data-handle-key'?: number;
  onClick?: () => void;
  onKeyDown?: (event: unknown) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onTouchStart?: (e: React.TouchEvent<HTMLInputElement>) => void;
};

const ARROW_UP = 38;
const ARROW_DOWN = 40;

const getAriaLabel = (dataHandleKey: number | undefined): string => {
  return dataHandleKey === 0 ? t`Slider handle (minimum)` : t`Slider handle (maximum)`;
};

const SliderHandle = ({
  handleRef = () => {},
  'data-handle-key': dataHandleKey,
  'aria-valuenow': ariaValuenow,
  'aria-valuemin': ariaValuemin,
  'aria-valuemax': ariaValuemax,
  onKeyDown,
  onMouseDown,
  onTouchStart,
  ...props
}: Props): JSX.Element => {
  const prefix = useContext(PrefixContext);
  const isPointerDragging = useRef(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      isPointerDragging.current = true;
      onMouseDown?.(e);
    },
    [onMouseDown]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLInputElement>) => {
      isPointerDragging.current = true;
      onTouchStart?.(e);
    },
    [onTouchStart]
  );

  useEffect(() => {
    const handlePointerUp = (): void => {
      isPointerDragging.current = false;
    };
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchend', handlePointerUp);
    return () => {
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchend', handlePointerUp);
    };
  }, []);

  // VoiceOver swipe up/down gestures change the native <input type="range">
  // value rather than dispatching keyboard events. Without this handler,
  // Rheostat (which only listens for onKeyDown) never sees the change.
  // We translate the native value change into a synthetic keyboard event
  // so Rheostat processes it through its normal path.
  // During pointer (mouse/touch) drags, Rheostat handles positioning directly,
  // so we skip this to avoid conflicting with Rheostat's drag logic.
  const handleNativeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isPointerDragging.current) return;

      const newValue = Number(e.target.value);
      if (newValue === ariaValuenow || !onKeyDown) return;

      const keyCode = newValue > (ariaValuenow ?? 0) ? ARROW_UP : ARROW_DOWN;
      onKeyDown({
        keyCode,
        currentTarget: e.currentTarget,
        preventDefault: () => {},
        stopPropagation: () => {},
      });
    },
    [ariaValuenow, onKeyDown]
  );

  return (
    <input
      type="range"
      ref={handleRef}
      data-handle-key={dataHandleKey}
      aria-label={getAriaLabel(dataHandleKey)}
      aria-valuenow={ariaValuenow}
      aria-valuetext={`${prefix}${ariaValuenow}`}
      aria-valuemin={ariaValuemin}
      aria-valuemax={ariaValuemax}
      min={ariaValuemin}
      max={ariaValuemax}
      value={ariaValuenow}
      onChange={handleNativeChange}
      onKeyDown={onKeyDown}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      {...props}
    />
  );
};

export default SliderHandle;
